import React, { useCallback, useMemo } from "react";
import { createEditor } from "slate";
import { Slate, Editable, withReact } from "slate-react";
import type { Media } from "@bronte/viewer";
import { StageElement, type ViewerDeviceRuntime, type ThemeVariant } from "../../../types";
import { RichTextEditor } from "@pearsontechnology/aero-components-v2";
import { inferBronteThemeWrapperClass } from "../../../util/bronteThemeClass";
import {
  TableContainer,
  TableLabelSection,
  TableInstructions,
  StyledTable,
  StyledCell,
  StyledCaption,
  GRID_LINES_DISPLAY,
  DEFAULT_GRIDLINE_COLOR,
  getGridlineDisplayClass,
} from "./styles";
import { sanitizeTableNodes } from "./sanitizeTable";
import { normalizeInitialValue } from "../../../util/normalizeSlateValue";
import ColGroup from "./ColGroup";

// Import text element plugins
import InlineIcon from "../TextElement/Plugins/InlineIcon";
import Link from "../TextElement/Plugins/Link/Plugin";
import ReferenceLink from "../TextElement/Plugins/ReferenceLink/Plugin";
import MathMLExpressionDefault from "../TextElement/Plugins/MathMLExpressionDefault";
import CollapsibleText from "../TextElement/Plugins/CollapsibleText";
import KeyTerm from "../TextElement/Plugins/KeyTerm";
import Footnote from "../TextElement/Plugins/Footnote";
import PopupTip from "../TextElement/Plugins/PopupTip";
import SpotlightBlockIds from "../TextElement/Plugins/SpotlightBlockIds/Plugin";
import LegacyInline from "../TextElement/Plugins/LegacyInline/Plugin";
import type { InlineIconConfig } from "../TextElement/Plugins/InlineIcon/DefaultConfig";

interface TableElementViewerProps {
  element: StageElement & { value?: any; config?: any };
  onClickReferenceLink?: (dataId: string, referenceId: string) => void;
  onMediaClick?: (data: Media) => void;
  viewerDevice?: ViewerDeviceRuntime;
  themeVariant?: Partial<ThemeVariant>;
}

// Constants for cell types
const CELL_TAGS = {
  TD: "table-data",
  TH: "table-header",
};

const TABLE_TAGS = {
  TABLE: "table",
  THEAD: "table-head",
  TBODY: "table-body",
  TR: "table-row",
};

// Helper function to flatten nodes
const flattenNodes = (node: any): any[] => {
  if (!node || !node.children) return [node].filter(Boolean);
  return [
    node,
    ...(node.children || []).reduce(
      (acc: any[], currNode: any) => [...acc, ...flattenNodes(currNode)],
      [],
    ),
  ];
};

// Helper function to get canonical table structure
// Based on the implementation in bronte-lib-stage-presentation-framework
const getCanonicalTable = (rowNodes: any[]): any[][] => {
  const matrix: any[][] = [];

  rowNodes.forEach((rowNode, rowIdx) => {
    let colIdx = 0;
    if (!matrix[rowIdx]) {
      matrix[rowIdx] = [];
    }

    const cells = (rowNode.children || []).filter(
      (node: any) => node.type === CELL_TAGS.TD || node.type === CELL_TAGS.TH,
    );

    cells.forEach((cell: any) => {
      const colSpan = getValidSpan(cell.data?.colSpan);
      const rowSpan = getValidSpan(cell.data?.rowSpan);

      let colSpanIdx = 0;
      let hasSetInitial = false;
      let colOffset = 0;

      for (let spanIdx = rowIdx; spanIdx < rowIdx + rowSpan; spanIdx++) {
        if (!matrix[spanIdx]) {
          matrix[spanIdx] = [];
        }

        for (colSpanIdx = 0; colSpanIdx < colSpan; colSpanIdx++) {
          // Insert cell at first empty position
          let i = 0;
          while (
            matrix[spanIdx][hasSetInitial ? colOffset + colSpanIdx + i : colIdx + colSpanIdx + i]
          ) {
            i++;
          }
          const fakeCell = colSpanIdx > 0 || spanIdx !== rowIdx;
          let col = colIdx + colSpanIdx + i;
          if (!hasSetInitial) {
            colOffset = colIdx + colSpanIdx + i;
            hasSetInitial = true;
          } else {
            col = colOffset + colSpanIdx + i;
          }
          matrix[spanIdx][col] = {
            coordinates: [spanIdx, col],
            ref: cell,
            virtual: fakeCell,
          };
        }
      }
      colIdx += colSpanIdx;
    });
  });

  return matrix;
};

// Helper to validate span values
const getValidSpan = (spanValue: any): number => {
  const newSpanValue = typeof spanValue === "string" ? parseInt(spanValue) : spanValue;
  return isNaN(newSpanValue) || newSpanValue < 1 ? 1 : newSpanValue;
};

const TEXT_ALIGN_BY_HORIZONTAL_ALIGN: Record<string, React.CSSProperties["textAlign"]> = {
  start: "left",
  left: "left",
  center: "center",
  end: "right",
  right: "right",
};

const ALIGN_CONTENT_BY_VERTICAL_ALIGN: Record<string, React.CSSProperties["alignContent"]> = {
  "flex-start": "flex-start",
  top: "flex-start",
  center: "center",
  middle: "center",
  "flex-end": "flex-end",
  bottom: "flex-end",
};

const getCellAlignment = (cellNode: any) => {
  const isHeaderNode = cellNode.type === CELL_TAGS.TH;
  const data = cellNode.data;
  const horizontalAlign = (data ? data.horizontalAlign : isHeaderNode && "center") || "start";
  const verticalAlign = (data ? data.verticalAlign : isHeaderNode && "center") || "flex-start";
  const editorStyle: React.CSSProperties = {
    textAlign: TEXT_ALIGN_BY_HORIZONTAL_ALIGN[horizontalAlign] ?? "left",
    alignContent: ALIGN_CONTENT_BY_VERTICAL_ALIGN[verticalAlign] ?? "flex-start",
  };
  return { horizontalAlign, editorStyle };
};

// Cell content renderer using the same plugins as OldTextElement
const CellContent: React.FC<{
  cellNode: any;
  onClickReferenceLink?: (dataId: string, referenceId: string) => void;
  onMediaClick?: (data: Media) => void;
  viewerDevice?: ViewerDeviceRuntime;
  pluginConfigs?: Record<string, InlineIconConfig>;
  editorStyle?: React.CSSProperties;
}> = ({
  cellNode,
  onClickReferenceLink,
  onMediaClick,
  viewerDevice,
  pluginConfigs,
  editorStyle,
}) => {
  // Cell content in new format is directly cellNode.children
  const cellContent = cellNode.children || [];

  const plugins = useMemo(
    () => [
      SpotlightBlockIds(),
      RichTextEditor.Plugins.Paragraph.Plugin(),
      RichTextEditor.Plugins.BoldItalicUnderline.Plugin(),
      RichTextEditor.Plugins.CustomParagraph.Plugin(),
      RichTextEditor.Plugins.LineHeight.Plugin(),
      RichTextEditor.Plugins.AutoInsertLists.Plugin(),
      RichTextEditor.Plugins.Headings.Plugin(),
      RichTextEditor.Plugins.SuperSubScript.Plugin(),
      RichTextEditor.Plugins.Highlight.Plugin(),
      RichTextEditor.Plugins.TextColor.Plugin(),
      RichTextEditor.Plugins.StyledText.Plugin(true),
      PopupTip.Plugin(onMediaClick, viewerDevice),
      InlineIcon.Plugin({ pluginConfigs, viewerDevice }),
      Link(),
      ReferenceLink(onClickReferenceLink),
      MathMLExpressionDefault.Plugin(),
      CollapsibleText.Plugin(),
      KeyTerm.Plugin(),
      Footnote.Plugin(),
      LegacyInline(),
    ],
    [onClickReferenceLink, onMediaClick, viewerDevice, pluginConfigs],
  );

  const editor = useMemo(() => {
    let baseEditor = withReact(createEditor());
    plugins.forEach((plugin) => {
      baseEditor = plugin(baseEditor);
    });
    return baseEditor;
  }, [plugins]);

  const normalizedContent = useMemo(
    () => normalizeInitialValue(editor, cellContent),
    [editor, cellContent],
  );

  const renderElement = useCallback(
    (props: any) => {
      for (const plugin of plugins) {
        if (plugin.renderElement) {
          const result = plugin.renderElement(props);
          if (result) return result;
        }
      }
      const { attributes, children } = props;
      return <div {...attributes}>{children}</div>;
    },
    [plugins],
  );

  const renderLeaf = useCallback(
    (props: any) => {
      let { children } = props;
      if (props.leaf.bold) children = <strong>{children}</strong>;
      if (props.leaf.italic) children = <em>{children}</em>;
      if (props.leaf.underline) children = <u>{children}</u>;
      if (props.leaf.code) children = <code>{children}</code>;
      if (props.leaf.strikethrough) children = <s>{children}</s>;

      for (const plugin of plugins) {
        if (plugin.renderLeaf) {
          const result = plugin.renderLeaf({ ...props, children });
          if (result !== undefined) children = result;
        }
      }
      return <span {...props.attributes}>{children}</span>;
    },
    [plugins],
  );

  if (!normalizedContent.length) return <div />;

  return (
    <Slate editor={editor} initialValue={normalizedContent}>
      <Editable
        readOnly={true}
        style={editorStyle}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
      />
    </Slate>
  );
};

function renderTableRow(
  row: any[],
  rowIndex: number,
  rowOffset: number,
  totalRows: number,
  gridlines: { display?: string; color?: string },
  headerColor: string | undefined,
  onClickReferenceLink?: (dataId: string, referenceId: string) => void,
  onMediaClick?: (data: Media) => void,
  viewerDevice?: ViewerDeviceRuntime,
  pluginConfigs?: Record<string, InlineIconConfig>,
) {
  return (
    <tr key={`row-${rowIndex}`}>
      {row.map((cell, colIndex) => {
        if (cell.virtual) return null;
        const cellNode = cell.ref;
        const rowSpan = cellNode.data?.rowSpan || 1;
        const colSpan = cellNode.data?.colSpan || 1;
        const isHeader = cellNode.type === CELL_TAGS.TH;
        const { horizontalAlign, editorStyle } = getCellAlignment(cellNode);
        return (
          <StyledCell
            key={`cell-${rowIndex}-${colIndex}`}
            as={isHeader ? "th" : "td"}
            $isHeader={isHeader}
            $horizontalAlign={horizontalAlign}
            $headerColor={headerColor}
            $rowIndex={rowIndex}
            $colIndex={colIndex}
            $rowSpan={rowSpan}
            $totalRows={totalRows}
            $gridlinesDisplay={gridlines.display}
            $gridlinesColor={gridlines.color}
            style={{
              gridRow: `${rowIndex + rowOffset + 1} / span ${rowSpan}`,
              gridColumn: `${colIndex + 1} / span ${colSpan}`,
            }}
          >
            <CellContent
              cellNode={cellNode}
              onClickReferenceLink={onClickReferenceLink}
              onMediaClick={onMediaClick}
              viewerDevice={viewerDevice}
              pluginConfigs={pluginConfigs}
              editorStyle={editorStyle}
            />
          </StyledCell>
        );
      })}
    </tr>
  );
}

const TableElementViewer: React.FC<TableElementViewerProps> = ({
  element,
  onClickReferenceLink,
  onMediaClick,
  viewerDevice,
  themeVariant,
}) => {
  const config = element.config || {};
  const pluginConfigs = config.pluginConfigs as Record<string, InlineIconConfig> | undefined;
  const componentId = element.ref || "";

  const tableNode = useMemo(() => {
    if (!element.value) return null;
    const cloned = structuredClone(element.value);
    sanitizeTableNodes(cloned);
    return cloned;
  }, [element.value]);

  if (!tableNode || tableNode.type !== TABLE_TAGS.TABLE) {
    return null;
  }
  const flattenedNodes = flattenNodes(tableNode);
  const allRowNodes = flattenedNodes.filter((node: any) => node.type === TABLE_TAGS.TR);

  if (allRowNodes.length === 0) {
    return null;
  }

  const theadNode = tableNode.children?.find((node: any) => node.type === TABLE_TAGS.THEAD);
  const hasHeaderRows = (theadNode?.children?.length ?? 0) > 0;

  const canonicalTable = getCanonicalTable(allRowNodes);

  const gridlines = {
    display: config.gridlines?.display || GRID_LINES_DISPLAY.all,
    color: config.gridlines?.color || DEFAULT_GRIDLINE_COLOR,
  };
  const gridlineClass = getGridlineDisplayClass(gridlines.display);
  const tableTitleText = config.tableTitle;
  const captionText = config.tableCaptionText || config.tableCaption?.caption;
  const captionsText = config.captions;
  const creditText = config.credit;
  const activityLabelText = config.activityLabel;
  const activityTitleText = config.activityTitle;
  const instructionsText = config.instructions;
  const headerColor = config.headerAndFooter?.headerBackgroundColor;
  const horizontalScroll = config.responsiveBehaviour === "scroll";
  const hasActivityItems = activityLabelText || activityTitleText;
  const customStyle = config.customStyle || {};
  const cssClass = customStyle.cssClass || "";
  const cssUrl = customStyle.cssUrl;
  const bronteThemeWrapperClass = useMemo(
    () => inferBronteThemeWrapperClass(themeVariant),
    [themeVariant],
  );
  const columnCount = useMemo(
    () => canonicalTable.reduce((max, row) => Math.max(max, row.length), 0),
    [canonicalTable],
  );
  const rowOffset = (tableTitleText ? 1 : 0) + (captionText ? 1 : 0);
  const dataRowCount = canonicalTable.length;
  const hasBelowCaption = Boolean(captionsText || creditText);

  const rowsSize =
    dataRowCount + (tableTitleText ? 1 : 0) + (captionsText ? 1 : 0) + (creditText ? 1 : 0);
  const belowCaptionRow = allRowNodes.length + 3;

  return (
    <TableContainer
      className={`spf-rce-table ${bronteThemeWrapperClass} ${cssClass}${
        horizontalScroll ? " table-overflow-scroll" : ""
      }`}
      $headerColor={headerColor}
      $numCols={columnCount}
    >
      {cssUrl && <style>{`@import url("${cssUrl}");`}</style>}

      {hasActivityItems && (
        <TableLabelSection className="component-label-section">
          {activityLabelText && (
            <span
              className="component-label"
              data-id={`${componentId}-activityLabel`}
              dangerouslySetInnerHTML={{ __html: activityLabelText }}
            />
          )}
          {activityTitleText && (
            <span
              className="component-title"
              data-id={`${componentId}-activityTitle`}
              dangerouslySetInnerHTML={{ __html: activityTitleText }}
            />
          )}
        </TableLabelSection>
      )}

      {instructionsText && (
        <TableInstructions
          data-id={`${componentId}-instructions`}
          dangerouslySetInnerHTML={{ __html: instructionsText }}
        />
      )}

      <StyledTable
        $numCols={columnCount}
        $rowsSize={rowsSize}
        $horizontalScroll={horizontalScroll}
        $gridlinesDisplay={gridlines.display}
        $gridlinesColor={gridlines.color}
        className={gridlineClass}
        data-id={`${componentId}-${gridlineClass}`}
      >
        {tableTitleText && (
          <StyledCaption
            $isTitle
            $row={1}
            role="heading"
            className="title table-title"
            data-id={`${componentId}-tableTitle`}
            dangerouslySetInnerHTML={{ __html: tableTitleText }}
          />
        )}
        {captionText && (
          <StyledCaption
            className="table-caption"
            $row={tableTitleText ? 2 : 1}
            data-id={`${componentId}-tableCaptionText`}
            dangerouslySetInnerHTML={{ __html: captionText }}
          />
        )}
        <ColGroup numCols={columnCount} numRows={dataRowCount} />
        <thead>
          {canonicalTable.map((row, rowIndex) =>
            hasHeaderRows && rowIndex === 0
              ? renderTableRow(
                  row,
                  rowIndex,
                  rowOffset,
                  dataRowCount,
                  gridlines,
                  headerColor,
                  onClickReferenceLink,
                  onMediaClick,
                  viewerDevice,
                  pluginConfigs,
                )
              : null,
          )}
        </thead>
        <tbody>
          {canonicalTable.map((row, rowIndex) =>
            !hasHeaderRows || rowIndex > 0
              ? renderTableRow(
                  row,
                  rowIndex,
                  rowOffset,
                  dataRowCount,
                  gridlines,
                  headerColor,
                  onClickReferenceLink,
                  onMediaClick,
                  viewerDevice,
                  pluginConfigs,
                )
              : null,
          )}
        </tbody>
        {hasBelowCaption && (
          <StyledCaption className="belowTable" $belowTable $row={belowCaptionRow}>
            {captionsText && (
              <div
                className="component-caption"
                data-id={`${componentId}-captions`}
                dangerouslySetInnerHTML={{ __html: captionsText }}
              />
            )}
            {creditText && (
              <div
                className="component-credit"
                data-id={`${componentId}-credit`}
                dangerouslySetInnerHTML={{ __html: creditText }}
              />
            )}
          </StyledCaption>
        )}
      </StyledTable>
    </TableContainer>
  );
};

export default TableElementViewer;
